import { buildExcelBlob, type BuildExcelOptions, type WorkbookItem } from './excel-workbook';
import { isLowQualityPhoto } from './findings';
import { receiptFilenameStem, uniqueSheetName } from './filename';
import type { ReceiptSession } from './types';

/**
 * Standalone .xlsx download — same workbook shape as the `receipts.xlsx`
 * inside the ZIP export (Summary sheet + per-receipt breakdown sheets).
 * The shared layout lives in `excel-workbook.ts`; this file's only job
 * is to turn `ReceiptSession[]` into the `WorkbookItem[]` the builder
 * expects, which means handing it the original upload filename and a
 * sanitised, dedup'd sheet name per receipt.
 */

function planItems(sessions: readonly ReceiptSession[]): WorkbookItem[] {
  // Sheet names are capped at 31 chars by Excel — uniqueSheetName
  // truncates leaving room for a dedupe suffix, then dedupes.
  const sheetSeen = new Map<string, number>();
  const items: WorkbookItem[] = [];
  for (const session of sessions) {
    if (session.state.kind !== 'success' || session.receipt === null) continue;
    const r = session.receipt;
    const stem = receiptFilenameStem(r.vendor.value, r.date.value);
    const sheetName = uniqueSheetName(stem, sheetSeen);
    items.push({
      receipt: r,
      displayName: session.filename,
      sheetName,
      lowQuality: isLowQualityPhoto(session),
    });
  }
  return items;
}

export async function exportXlsxBatch(
  sessions: readonly ReceiptSession[],
  opts: BuildExcelOptions = {},
): Promise<Blob> {
  return buildExcelBlob(planItems(sessions), opts);
}
